import type { ReactElement } from 'react';
import type { ParamValue } from '@gr/core/platform/config/registry';
import type { PersonalSettingDto, PersonalSettingsDto } from '@gr/core/platform/user-preferences/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PersonalSettings, type PersonalOutcome } from './PersonalSettings/PersonalSettings';

/**
 * PREF-UC-3, PREF-UC-3.CC-2: личные настройки в профиле. После сохранения эта вкладка перечитывает
 * конфигурацию сама, остальные вкладки пользователя — по событию configChanged.
 */
export function PersonalSettingsSection(): ReactElement {
  const { api, config } = useServices();
  const { t, te } = useT();
  const settings = useResource(() => api.preferences.personalSettings(), [api]);

  const apply = async (result: OperationResult<PersonalSettingDto>): Promise<PersonalOutcome> => {
    if (!result.success || !result.data) return { ok: false, errors: result.messages };
    const setting = result.data;
    const current = settings.data;
    if (current) settings.setData({ settings: current.settings.map((item) => (item.key === setting.key ? setting : item)) } satisfies PersonalSettingsDto);
    await config.load();
    return { ok: true };
  };

  return (
    <Card title={t('preferences.personal.title')}>
      <p className="u-muted u-small">{t('preferences.personal.subtitle')}</p>
      {settings.loading && !settings.data ? (
        <Loading label={t('common.loading')} />
      ) : settings.error || !settings.data ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          {te(settings.error)}
        </Alert>
      ) : (
        <PersonalSettings
          settings={settings.data.settings}
          onSave={async (key: string, value: ParamValue) => apply(await api.preferences.savePersonalSetting(key, value))}
          onReset={async (key: string) => apply(await api.preferences.resetPersonalSetting(key))}
        />
      )}
    </Card>
  );
}
